import { useEffect, useState } from 'preact/hooks';
import type { LiveItemDto } from '../../shared/types';
import { api } from '../api';
import { Artwork } from './Artwork';
import { isLive, liveItem, playLive } from '../state/player';

export function LiveCarousel() {
  const [items, setItems] = useState<LiveItemDto[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchLive = () => {
      if (typeof api?.live !== 'function') {
        if (!cancelled) {
          setItems([]);
          setLoading(false);
        }
        return;
      }
      api
        .live()
        .then((res) => {
          if (cancelled) return;
          const liveList = res?.liveItems ?? res?.items ?? [];
          setItems(liveList);
        })
        .catch(() => {
          if (!cancelled) setItems([]);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    };

    fetchLive();
    // Poll for live stream updates every 30 seconds
    const interval = setInterval(fetchLive, 30_000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  if (loading || items.length === 0) {
    return null;
  }

  return (
    <section class="live-carousel" aria-label="Live podcasts">
      <div class="live-carousel__header">
        <span class="live-carousel__badge live-pulse">🔴 LIVE NOW</span>
      </div>
      <div class="live-carousel__list" role="list">
        {items.map((item) => {
          const isCurrentLive = isLive.value && liveItem.value?.id === item.id;
          const artworkUrl = item.imageUrl ?? item.feedImageUrl ?? item.feed?.imageUrl;
          const feedTitle = item.feedTitle ?? item.feed?.title ?? 'Podcast';

          return (
            <div key={item.id} class="live-card" role="listitem">
              <div class="live-card__artwork-wrapper">
                <Artwork
                  src={artworkUrl}
                  alt=""
                  size={64}
                  className="live-card__artwork"
                />
                <span class="live-card__badge live-pulse" aria-hidden="true">
                  🔴 LIVE
                </span>
              </div>
              <div class="live-card__content">
                <span class="live-card__feed">{feedTitle}</span>
                <h3 class="live-card__title" title={item.title}>
                  {item.title}
                </h3>
              </div>
              <div class="live-card__actions">
                <button
                  type="button"
                  class={`button button--primary live-card__tune-in ${isCurrentLive ? 'is-playing' : ''}`}
                  onClick={() => playLive(item)}
                  aria-label={`Tune In to ${item.title}`}
                >
                  {isCurrentLive ? 'Listening' : 'Tune In'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
